import { importLibrary, setOptions } from '@googlemaps/js-api-loader';

// Audience-builder — the ONE place the Google Maps JS API is configured + loaded.
// Both the map (maps + marker libraries) and the geo-centre resolver (geocoding
// library) go through here so `setOptions` is called exactly once (it must run
// before any library loads) and the same key/version are shared.
//
// GRACEFUL DEGRADATION: with no VITE_GOOGLE_MAPS_API_KEY everything no-ops — the
// map renders a notice and the geocoder returns null (cities just stay chips).

export const MAPS_API_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? '';

// A VECTOR Map ID is REQUIRED for Data-Driven Styling boundaries (country/region
// outlines). Created in the Cloud Console with the COUNTRY + ADMINISTRATIVE_AREA_LEVEL_1
// feature layers enabled on the map style. When unset the map stays raster and area
// boundaries simply aren't drawn (graceful) — pins/cities/places still work.
export const MAPS_MAP_ID = (import.meta.env.VITE_GOOGLE_MAPS_MAP_ID as string | undefined) ?? '';

let configured = false;
function configure(): void {
  if (configured || !MAPS_API_KEY) return;
  setOptions({ key: MAPS_API_KEY, v: 'weekly' });
  configured = true;
}

// Load the map + marker libraries and resolve the global `google` namespace.
let mapsPromise: Promise<typeof google> | null = null;
export function loadMaps(): Promise<typeof google> {
  if (!mapsPromise) {
    configure();
    mapsPromise = Promise.all([importLibrary('maps'), importLibrary('marker')]).then(() => google);
  }
  return mapsPromise;
}

// Lazily construct a single Geocoder (the geocoding library is loaded on demand —
// only the first city resolution pays for it).
let geocoderPromise: Promise<google.maps.Geocoder> | null = null;
export function getGeocoder(): Promise<google.maps.Geocoder> {
  if (!geocoderPromise) {
    configure();
    geocoderPromise = importLibrary('geocoding').then((lib) => new lib.Geocoder());
  }
  return geocoderPromise;
}
